import * as React from 'react';
import { assignRefs } from '../../modules/react-utils';
import useIsMount from '../../hooks/useIsMount';
import { PortalContext } from './Portal/Portal';

type ClosableProps = {
    children?: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement> & {
        onClose: () => void;
        includeElements?: Array<HTMLElement | React.RefObject<HTMLElement> | string>;
    };

function Closable(props: ClosableProps, ref: React.Ref<HTMLDivElement>) {
    const { onClose, includeElements = [], children, ...rest } = props;
    const containerRef = React.useRef<HTMLDivElement>();
    const isMount = useIsMount();
    const portalContext = React.useContext(PortalContext);

    React.useEffect(() => {
        const isContainerContainsElement = (target: HTMLElement) => {
            let isContains = containerRef.current?.contains(target);
            if (!isContains) {
                isContains = includeElements.some((obj) => {
                    let elements: HTMLElement[] = [];
                    if (typeof obj === 'string') {
                        elements = Array.from(document.querySelectorAll(obj));
                    } else {
                        const curEl = 'current' in obj ? obj.current : obj;
                        if (curEl) {
                            elements = [curEl];
                        }
                    }
                    return elements.some((el) => target === el || el?.contains(target));
                });
            }
            if (!isContains) {
                isContains = portalContext?.contains(target);
            }
            return isContains;
        };

        const getTarget = (e: Event) => (e.composedPath()[0] ?? e.target) as HTMLElement;

        const clickHandler = (e: Event) => {
            if (isMount() && !isContainerContainsElement(getTarget(e))) {
                onClose();
            }
        };
        const blurHandler = (e: Event) => {
            // if the main frame is blurred, the user clicked on the preview iframe (or clicked out of the tab or browser)
            // use isWindowVisible to differentiate between moving focus into an iframe, or moving focus from the browser/tab
            if (isMount() && e.target === window) {
                setTimeout(() => {
                    if (document.activeElement?.tagName === 'IFRAME') {
                        onClose();
                    }
                }, 0);
            }
        };
        const escapeHandler = (e: KeyboardEvent) => {
            if (isMount() && e.key === 'Escape' && !isContainerContainsElement(getTarget(e))) {
                onClose();
            }
        };

        // register events in timeout to prevent click propagation (e.g. something onClick renders this component - react 18 keeps same event so clickHandler)
        // will be executed here as well
        const timeout = setTimeout(() => {
            window.addEventListener('blur', blurHandler, true);
            document.body.addEventListener('click', clickHandler, true);
            document.body.addEventListener('touchend', clickHandler, true);
            document.body.addEventListener('keydown', escapeHandler, true);
        });

        return () => {
            window.clearTimeout(timeout);
            window.removeEventListener('blur', blurHandler, true);
            document.body.removeEventListener('click', clickHandler, true);
            document.body.removeEventListener('touchend', clickHandler, true);
            document.body.removeEventListener('keydown', escapeHandler, true);
        };
    }, [onClose, isMount, includeElements, portalContext]);

    return (
        <div ref={assignRefs(ref, containerRef)} {...rest} onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
            {children}
        </div>
    );
}

export default React.forwardRef(Closable);
